















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import moment from 'moment';
import { EntityStatus, WaterMetersApi, WaterMonitorsApi, WaterTanksApi } from '@/api';
import { checkDateAfter2010 } from '@/common/DateFilterUtil';

Vue.component('VueCtkDateTimePicker', VueCtkDateTimePicker);

@Component({
	components: {},
})
export default class WaterEntityChartAdmin extends Vue {
	@Prop({ default: true })
	public useAxisOptions: boolean;

	@Prop()
	public entityType?: string;

	@Prop()
	public entities?: any[];

	@Prop()
	public valueField?: Array<{
		series: string;
		title: string;
		processor: () => any;
		hidden?: boolean;
	}>;
	public moment = moment;

	public form = {
		start: '',
		end: '',
	};
	public graph: { [x: string]: { [x: string]: { [x: string]: any } } };
	public chart: am4charts.XYChart;
	public tempValueAxis: any;
	public offset = 0;
	public nDays = 7;
	public nDaysValues = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

	public collapsed: boolean = false;
	private waterMetersApi: WaterMetersApi = new WaterMetersApi();
	private waterTanksApi: WaterTanksApi = new WaterTanksApi();
	private waterMonitorsApi: WaterMonitorsApi = new WaterMonitorsApi();
	private opposite = true;
	public useMultiAxis = false;

	public options = [
		{ text: 'Single Axis', value: false },
		{ text: 'Multiple Axis', value: true },
	];

	public get localStorageKey() {
		return `WATER_ENTITY_OVERVIEW_SWITCH_${this.entityType}`;
	}

	public mounted() {
		if (this.useAxisOptions) {
			if (this.entityType === 'water_tanks') this.useMultiAxis = true;
			this.useMultiAxis = localStorage.getItem(this.localStorageKey) == 'true';
		}
	}

	@Watch('useMultiAxis')
	public handleRadioClick() {
		localStorage.setItem(this.localStorageKey, `${this.useMultiAxis}`);
		this.loadChart();
	}

	// offset = current date - end date(offset is how far back it will look)
	// nDays = end date - start date (nDays is how many days back from the offset)
	public updateRange(start: moment.MomentInput, end: moment.MomentInput) {
		const a = moment(end, 'DD/MM/YYYY hh:mm:ss').local();
		const b = moment(start, 'DD/MM/YYYY hh:mm:ss').local();
		const c = moment().endOf('day');
		this.offset = c.diff(a, 'days');
		this.nDays = a.diff(b, 'days') + 1;
	}

	public updateForm() {
		if (this.form.end !== null && this.form.start !== null) {
			this.updateRange(this.form.start, this.form.end);
			this.graphData();
		}
	}

	// am4core.disposeAllCharts();
	public beforeDestroy() {
		if (this.chart) {
			this.chart.dispose();
		}
	}

	@Watch('entities', { immediate: true })
	public async graphData() {
		this.graph = {};
		if (!this.entities) {
			return;
		}
		for (const index of this.entities.filter(
			(e: any) => ![EntityStatus.Cancelled, EntityStatus.Removed].includes(e?.status ?? null),
		)) {
			const entityId = index.id;
			try {
				let data: any = null;
				switch (this.entityType) {
					case 'water_meters':
						data = (
							await this.waterMetersApi.getWaterMeterGraphData(
								parseInt(entityId, 10),
								this.nDays + 2,
								'' + ((this.offset || 0) - 1),
							)
						).data;
						break;
					case 'water_tanks':
						data = (
							await this.waterTanksApi.getWaterTankGraphData(
								parseInt(entityId, 10),
								this.nDays + 2,
								'' + ((this.offset || 0) - 1),
							)
						).data;
						break;
					case 'water_monitors':
						data = (
							await this.waterMonitorsApi.getWaterMonitorGraphData(
								parseInt(entityId, 10),
								this.nDays + 2,
								'' + ((this.offset || 0) - 1),
							)
						).data;
						break;
				}
				if (data != null) {
					Object.keys(data.data).forEach(key => {
						this.graph[`${entityId}-${key}`] = data.data[key].filter((point: any) => {
							return checkDateAfter2010(new Date(point.date));
						});
					});
				}
			} catch (e) {
				console.log(e);
			} finally {
			}
		}
		this.loadChart();
	}

	@Watch('offset', { immediate: false })
	public async updateOffset() {
		this.graphData();
	}

	@Watch('nDays', { immediate: false })
	public updateNDays() {
		this.graphData();
	}

	// Adds an invisible series with one point that is used as an end date for the graph.
	// Date will change dependant on date range selected
	public loadGraphEndBullet() {
		if (this.form.start == '' && this.form.end == '') return;

		const endpointDate: Date = moment(this.form.end, 'DD-MM-YYYY hh:mm:ss')
			.endOf('day')
			.toDate();
		const endpointSeries = this.chart.series.push(new am4charts.LineSeries());
		endpointSeries.hiddenInLegend = true;

		const endpointData = [
			{
				date: endpointDate,
				[`${this.entityType}-endpoint`]: 0,
			},
		];

		endpointSeries.dataFields.valueY = `${this.entityType}-endpoint`;
		endpointSeries.dataFields.dateX = 'date';
		endpointSeries.data = endpointData;
	}

	// API returns data from current time to x days ahead; eg 3pm 5/9/12 - 3pm 7/9/12. We need data
	// from 12am 5/9/12am - 11:59pm 7/9/12. We call for data a day either side and filter
	public filterChartData(chartData: any) {
		if (this.form.start == '' && this.form.end == '') return chartData;

		const startPoint = moment(this.form.start, 'DD-MM-YYYY hh:mm:ss')
			.startOf('day')
			.toDate();
		const endPoint = moment(this.form.end, 'DD-MM-YYYY hh:mm:ss')
			.endOf('day')
			.toDate();

		let newChartData: any[] = [];
		chartData.forEach((element: any) => {
			if (startPoint < element.date && endPoint > element.date) {
				newChartData.push(element);
			}
		});

		return newChartData;
	}

	public async loadChart() {
		// Themes
		// am4core.useTheme(am4themes_animated);

		if (this.chart) {
			await this.chart.dispose();
		}
		// Create chart instance
		this.chart = am4core.create(
			this.$refs[`chartdiv${this.entityType}${this.$route.params.productId}`],
			am4charts.XYChart,
		);

		// Increase contrast by taking evey second color
		this.chart.colors.step = 2;

		// this.chart = am4core.options.onlyShowOnViewport = true;
		// Create axes
		const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
		dateAxis.renderer.minGridDistance = 50;
		dateAxis.baseInterval = { timeUnit: 'minute', count: 5 }; // timeUnit: "minute", count: 5
		// dateAxis.dateFormats.setKey("day", "HH:mm/MMM dd");
		dateAxis.tooltipDateFormat = '[bold]dd-MM-yy, h:mm:ss a[/]';
		// dateAxis.dateFormats.setKey('minute', 'h:mm a');
		dateAxis.renderer.grid.template.location = 0;
		dateAxis.renderer.axisFills.template.disabled = false;
		dateAxis.renderer.axisFills.template.fill = am4core.color('#F6861F');
		dateAxis.renderer.axisFills.template.fillOpacity = 0.2;

		let dateFlag = true;
		let dayTemp = 0;
		dateAxis.fillRule = (dataItem: any) => {
			if (dayTemp !== dataItem.date.getDay()) {
				dayTemp = dataItem.date.getDay();
				dateFlag = !dateFlag;
			}
			dataItem.axisFill.visible = dateFlag;
		};
		// this.chart.scrollbarY = new am4core.Scrollbar();

		// Add legend and cursor
		this.chart.legend = new am4charts.Legend();
		this.chart.cursor = new am4charts.XYCursor();

		if (this.entities) {
			const sharedValueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());

			this.entities
				.filter(
					(e: any) => ![EntityStatus.Cancelled, EntityStatus.Removed].includes(e?.status ?? null),
				)
				.forEach((entity, index) => {
					if (this.entityType != null && this.valueField) {
						const entitySeries = this.entitySeries(entity.position)[this.entityType];
						if (entitySeries != null) {
							this.valueField.forEach((valueField, i: number) => {
								this.createSeries(
									this.useAxisOptions && !this.useMultiAxis
										? sharedValueAxis
										: this.chart.yAxes.push(new am4charts.ValueAxis()),
									false,
									`${entity.id}-${entitySeries.key}`,
									valueField.series,
									valueField ? valueField.processor : null,
									entity.name + ' ' + (valueField ? valueField.title : ''),
									'rectangle',
									undefined,
									valueField.hidden,
								);
							});
						}
					}
				});
		}

		this.loadGraphEndBullet();

		// const volumeAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
		// this.createSeries(volumeAxis, true, 'volume', 'Volume', false, 'circle', '#aaa');

		// this.chart.cursor.behavior = 'zoomXY';
		this.chart.cursor.xAxis = dateAxis;

		dateAxis.dateFormatter = new am4core.DateFormatter();
		dateAxis.dateFormatter.dateFormat = 'MM-dd';
		dateAxis.baseInterval = {
			timeUnit: 'second',
			count: 1,
		};
	}

	// Create series
	public createSeries(
		valueAxis: am4charts.ValueAxis,
		fillArea: boolean,
		field: string,
		seriesString: string,
		valueProcessor: ((val: any) => any) | null,
		name: string,
		shape: string,
		strokeWidth?: number,
		hidden?: boolean,
	) {
		const series = this.chart.series.push(new am4charts.LineSeries());
		series.dataFields.valueY = field;
		series.dataFields.dateX = 'date';
		series.strokeWidth = strokeWidth || 2;

		series.yAxis = valueAxis;

		series.name = name;
		series.tooltipText = '{name}: [bold]{valueY}[/]';
		series.hidden = hidden;
		valueAxis.disabled = hidden;

		let chartData: any[] = [];
		for (const row in this.graph[field]) {
			if (this.graph[field].hasOwnProperty(row)) {
				const newDate = new Date(this.graph[field][row].date);
				let label = '';
				let size = 0;
				const fill = am4core.color('#fff');
				if (this.graph[field][row].label) {
					label = this.graph[field][row].label;
					size = this.graph[field][row].size;
				}

				let value = this.graph[field][row][seriesString];
				if (valueProcessor != null) {
					value = valueProcessor(value);
				}
				const dataPoint = {
					date: newDate,
					label,
					size,
					[field]: value,
					fill,
				};
				chartData.push(dataPoint);
			}
		}
		chartData = this.filterChartData(chartData);

		series.data = chartData;
		series.autoGapCount = 60 * 20;
		series.connect = false;
		// labels
		if (field === 'volume' || field === 'height') {
			const bullet = series.bullets.push(new am4charts.CircleBullet());
			// bullet.circle.fill = am4core.color('#fff');
			bullet.circle.strokeWidth = 2;
			bullet.circle.propertyFields.radius = 'size';
			bullet.circle.propertyFields.fill = 'fill';

			const seriesLabel = series.bullets.push(new am4charts.LabelBullet());
			seriesLabel.label.text = '{label}';
			seriesLabel.label.horizontalCenter = 'left';
			seriesLabel.label.fontSize = 8;
			seriesLabel.label.dx = 5;
		}
		if (this.entityType === 'water_tanks' || field === '5-flow') {
			valueAxis.min = 0; //set y axis minimum to 0
		} else {
			valueAxis.extraMin = 0.1; //pad the y axis minimum by 10%
		}

		valueAxis.renderer.line.strokeOpacity = 1;
		valueAxis.renderer.line.strokeWidth = 2;
		valueAxis.renderer.line.stroke = series.stroke;
		valueAxis.renderer.labels.template.fill = series.stroke;

		valueAxis.renderer.opposite = this.opposite;
		this.opposite = !this.opposite;

		valueAxis.renderer.grid.template.disabled = true;
		series.events.on('hidden', this.toggleAxes);
		series.events.on('shown', this.toggleAxes);
	}
	private toggleAxes(ev: { target: { yAxis: any } }) {
		let axis = ev.target.yAxis;
		let disabled = true;
		axis.series.each(function(series: { isHiding: any; isHidden: any }) {
			if (!series.isHiding && !series.isHidden) {
				disabled = false;
			}
		});
		axis.disabled = disabled;
	}
	private entitySeries(entityPosition: string): any {
		return {
			water_meters: {
				key: 'flow',
			},
			water_tanks: {
				key: 'level',
			},
			water_monitors: {
				key: 'flow',
			},
		};
	}
}
